import { useState, useEffect } from 'react';
import { Modal, Table, Tag, Button } from '@bancobarigui/barihaus';
import { Empty, message } from 'antd';
import { VideoSession, VideoSessionParticipant, videoSessionService } from '@/services/videoSession.service';
import { MailOutlined } from '@ant-design/icons';

interface ManageParticipantsModalProps {
  open: boolean;
  videoSession: VideoSession | null;
  onClose: () => void;
  onSuccess?: () => void;
}

export function ManageParticipantsModal({
  open,
  videoSession,
  onClose,
}: ManageParticipantsModalProps) {
  const [participants, setParticipants] = useState<VideoSessionParticipant[]>([]);
  const [resendingEmails, setResendingEmails] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (open && videoSession) {
      setParticipants(videoSession.participants || []);
    }
  }, [open, videoSession]);

  const getRoleColor = (role: number) => {
    switch (role) {
      case 0: return 'blue'; // Inspector
      case 1: return 'green'; // Client
      case 2: return 'orange'; // Observer
      case 3: return 'purple'; // Provider
      case 4: return 'cyan'; // BankUser
      default: return 'default';
    }
  };

  const handleResendEmail = async (participantId: string, participantEmail: string) => {
    if (!videoSession) return;

    setResendingEmails(prev => new Set(prev).add(participantId));
    try {
      await videoSessionService.resendInviteEmail(videoSession.id, participantId);
      message.success(`Email reenviado para ${participantEmail} com sucesso!`);
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Erro ao reenviar email');
    } finally {
      setResendingEmails(prev => {
        const newSet = new Set(prev);
        newSet.delete(participantId);
        return newSet;
      });
    }
  };

  const columns = [
    {
      title: 'Nome',
      dataIndex: 'userName',
      key: 'userName',
      render: (text: string) => (
        text || <span style={{ color: '#999', fontStyle: 'italic' }}>Não informado</span>
      ),
    },
    {
      title: 'Email',
      dataIndex: 'userEmail',
      key: 'userEmail',
    },
    {
      title: 'Função',
      dataIndex: 'roleDescription',
      key: 'role',
      render: (text: string, record: VideoSessionParticipant) => (
        <Tag color={getRoleColor(record.role)}>{text || 'Não definida'}</Tag>
      ),
    },
    {
      title: 'Ações',
      key: 'actions',
      render: (_: any, record: VideoSessionParticipant) => (
        <Button
          type="link"
          icon={<MailOutlined />}
          onClick={() => handleResendEmail(record.id, record.userEmail)}
          loading={resendingEmails.has(record.id)}
          size="small"
        >
          Reenviar Email
        </Button>
      ),
    },
  ];

  if (!videoSession) {
    return null;
  }

  return (
    <Modal
      title={`Participantes - ${videoSession?.title || 'Sessão de Vídeo'}`}
      open={open}
      onCancel={onClose}
      footer={null}
      width={800}
    >
      <div className="space-y-4">
        <div>
          <h4 className="mb-2">Lista de Participantes ({participants.length})</h4>
          {participants.length === 0 ? (
            <Empty 
              description="Nenhum participante adicionado ainda"
            />
          ) : (
            <Table
              dataSource={participants}
              columns={columns as any}
              rowKey="id"
              pagination={false}
              size="small"
            />
          )}
        </div>

        <div style={{ 
          padding: '8px', 
          backgroundColor: '#fafafa', 
          borderRadius: '4px',
          fontSize: '12px',
          color: '#666'
        }}>
          <strong>Nota:</strong> Para adicionar participantes, use o agendamento da sessão. 
          Os participantes receberão o link da chamada por email.
        </div>
      </div>
    </Modal>
  );
}

